import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, App, Button, Collapse, Drawer, Progress, Select, Tooltip, Typography } from 'antd';

import RecordBadge from '../common/RecordBadge';
import DialogLoadState from '../common/DialogLoadState';
import { Skeleton } from '../loading';
import { CLOSE, PAPERCLIP } from '../icons';
import { formatCount } from '../../utils/counts';
import { formatBytes } from '../../utils/formatters';
import { isAttachAdmin } from '../../utils';
import { fileIdsOf, isFileElement } from '../../utils/fileValues';
import { serverFileRefusal } from '../../utils/fileUpload';
import { availableRules, globMatches, matchFiles, matchRequest } from '../../utils/bulkAttach/match';
import { planFiles, summarise } from '../../utils/bulkAttach/preflight';
import { runQueue } from '../../utils/bulkAttach/queue';
import { getItemAttachments, uploadItemAttachment } from '../../services/attachments';
import { fetchItemsByIds, fetchMatchingItems } from '../../services/itemsBulk';
import { isSeeOnlyElement } from '../../utils/elementAccess';
import { columnsOf } from '../../config/repeatingGroups';
import { saveItemCells, isStaleItem } from '../../services/items';
import { updateEntry } from '../../services/itemEntries';
import { entriesOf, entryName } from '../../utils/repeatingGroupEntries';
import { pickFolder } from '../../platform';
import useWindowClass from '../../hooks/useWindowClass';

const { Text, Paragraph } = Typography;

/**
 * ATTACH FILES TO MANY ITEMS AT ONCE (KPR-187; docs/guides/items/attach-files-
 * to-many-items.md). The general mechanism behind "an agent hands you a link
 * and you drop the files" (the epic's ruling: no one-scenario page) — opened
 * from the selection bar on the picked items, or from the page header's ⋯ on
 * the items listed.
 *
 * Three steps on one panel, and nothing moves until the person says so
 * (platform-standards §3, Batch actions, rule 1):
 *
 * 1. Choose — drop files or folders, or pick them; a phone's picker offers
 *    its photo library.
 * 2. Review — each file against the item it matched (utils/bulkAttach/match:
 *    by title, by an element's value, or by import id), what it will do
 *    (utils/bulkAttach/preflight: attach, skip — already there — or refused,
 *    with the reason in words, against the file it names: rule 2), and a
 *    picker to change the item or leave the file out.
 * 3. Upload — a few at a time (utils/bulkAttach/queue: a 429 waits, a dropped
 *    connection retries), each file's own progress, then a result that names
 *    every failure by file. Files go up through the ordinary attachment route
 *    under the person's session. Into a file element, each item then gets ONE
 *    merge PUT (the bulk write refuses file values), re-read first so a file
 *    someone added meanwhile is kept; if that bind is refused the files stay
 *    on the item as other attachments, and the result says so (rule 3).
 *
 * `request` (KPR-183) is an upload request an agent made: its entries (each an
 * item, an expected name or pattern, an element) pre-fill the pass — the rule
 * "As requested" matches files to entries, each file goes where its entry
 * says, and entries already answered take nothing more.
 *
 * `scope` is `{ kind: 'ids', ids }` (a hand-picked selection) or
 * `{ kind: 'filter', query, cardFilter, review, exclude }` (the listed items,
 * or "all N matching"). `canTakeFiles(item)` is the page's own rule for a row
 * that may take part (not linked, card writable, not locked); the rest are
 * left out and counted.
 */

const MAX_FILES = 1000;
const EXISTING_CONCURRENCY = 4;

let nextKey = 0;
const keyFor = () => `f${(nextKey += 1)}`;

/** Every file under a dropped folder, by the entries API (Chrome, Safari, Firefox). Hidden files (.DS_Store) are left out. */
async function filesFromDataTransfer(dataTransfer) {
	const out = [];
	const entries = [...(dataTransfer?.items || [])]
		.map(it => (typeof it.webkitGetAsEntry === 'function' ? it.webkitGetAsEntry() : null))
		.filter(Boolean);
	if (!entries.length) return [...(dataTransfer?.files || [])].map(file => ({ file, path: file.name }));

	const walk = async (entry, prefix) => {
		if (entry.name.startsWith('.')) return;
		if (entry.isFile) {
			const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
			out.push({ file, path: `${prefix}${entry.name}` });
			return;
		}
		if (entry.isDirectory) {
			const reader = entry.createReader();
			for (;;) {
				// readEntries hands back a batch at a time (100 in Chrome) until an empty one.
				// eslint-disable-next-line no-await-in-loop
				const batch = await new Promise((resolve, reject) => reader.readEntries(resolve, reject));
				if (!batch.length) break;
				// eslint-disable-next-line no-await-in-loop
				for (const child of batch) await walk(child, `${prefix}${entry.name}/`);
			}
		}
	};
	for (const entry of entries) {
		// eslint-disable-next-line no-await-in-loop
		await walk(entry, '');
	}
	return out;
}

export default function BulkAttachPanel({
	open,
	onClose,
	onAttached,
	collection = null,
	scope = null,
	cardsById = {},
	getElementsForCard = () => [],
	titleFor = item => item?.displayValue || '',
	canTakeFiles = () => true,
	request = null,
}) {
	const { modal, message } = App.useApp();
	const { compact: isMobile } = useWindowClass();
	const attachAdmin = isAttachAdmin();

	const [loading, setLoading] = useState(false);
	const [loadError, setLoadError] = useState(null);
	// The items in scope have answered (and not failed): until then the body is a skeleton.
	const [loaded, setLoaded] = useState(false);
	const [attempt, setAttempt] = useState(0);
	const [rawItems, setRawItems] = useState([]);
	const [truncated, setTruncated] = useState(false);
	const [files, setFiles] = useState([]);
	const [rule, setRule] = useState(request ? { kind: 'request' } : { kind: 'title' });
	const [destination, setDestination] = useState(null);
	const [overrides, setOverrides] = useState({});
	const [existing, setExisting] = useState({});
	const [phase, setPhase] = useState('review');
	const [progress, setProgress] = useState({});
	const [result, setResult] = useState(null);
	const [dragging, setDragging] = useState(false);
	const fileInput = useRef(null);
	const abortRef = useRef(null);

	// ---- the items in scope
	useEffect(() => {
		if (!open || !scope || !collection?._id) return undefined;
		let alive = true;
		setLoading(true);
		setLoaded(false);
		setLoadError(null);
		const load = scope.kind === 'ids'
			? fetchItemsByIds(scope.ids || []).then(items => ({ items, truncated: false }))
			: fetchMatchingItems({ collectionId: collection._id, query: scope.query, cardFilter: scope.cardFilter, review: scope.review, exclude: scope.exclude });
		load.then((r) => {
			if (!alive) return;
			setRawItems(r.items || []);
			setTruncated(Boolean(r.truncated));
			setLoaded(true);
		}).catch((err) => {
			if (alive) setLoadError(err?.response?.data?.message || 'The items could not be read. Try again.');
		}).finally(() => { if (alive) setLoading(false); });
		return () => { alive = false; };
	}, [open, scope, collection?._id, attempt]);

	// A fresh panel every time it opens.
	useEffect(() => {
		if (open) return;
		setFiles([]); setOverrides({}); setExisting({}); setPhase('review'); setProgress({}); setResult(null);
		setRule({ kind: 'title' }); setDestination(null); setRawItems([]); setTruncated(false);
	}, [open]);

	const items = useMemo(() => rawItems.filter(canTakeFiles), [rawItems, canTakeFiles]);
	const leftOut = rawItems.length - items.length;
	const itemsById = useMemo(() => {
		const map = {};
		for (const it of items) map[it._id] = { id: it._id, cardId: it.card_id || it.cardId, values: it.elements || {}, raw: it };
		return map;
	}, [items]);
	const matchItems = useMemo(() => items.map(it => ({
		id: it._id,
		cardId: it.card_id || it.cardId,
		title: titleFor(it),
		values: it.elements || {},
		externalId: it.source?.externalId,
	})), [items, titleFor]);

	const rules = useMemo(() => availableRules(matchItems, getElementsForCard), [matchItems, getElementsForCard]);

	// File elements the listed cards have, by name — a name shared across cards is one choice.
	const fileElements = useMemo(() => {
		const byName = new Map();
		for (const it of matchItems) {
			for (const el of getElementsForCard(it.cardId) || []) {
				// Not one a rule holds this person to "see only" (KPR-509): each
				// item's save would be refused after the files had uploaded.
				if (!isFileElement(el) || isSeeOnlyElement(el)) continue;
				const entry = byName.get(el.name) || { element: el, cards: new Set() };
				entry.cards.add(it.cardId);
				byName.set(el.name, entry);
			}
		}
		return [...byName.values()];
	}, [matchItems, getElementsForCard]);
	const elementFor = useCallback((cardId, name) => (getElementsForCard(cardId) || []).find(el => el.name === name) || null, [getElementsForCard]);
	const destinationEntry = destination ? fileElements.find(e => e.element.name === destination) : null;
	const destinationLabel = destinationEntry?.element?.label?.singular || destination;

	// ---- matching and the plan
	// A request's own rule is the default whenever the panel opens on one.
	// Keyed on the request's id, not the object: the page re-reads the request
	// after every pass (its counts move), and that must not undo a rule the
	// person chose.
	const requestId = request?._id || null;
	useEffect(() => { if (open && requestId) setRule({ kind: 'request' }); }, [open, requestId]);
	const byRequest = rule.kind === 'request' && Boolean(request);
	const matched = useMemo(() => {
		const list = files.map(f => ({ key: f.key, name: f.file.name }));
		return byRequest ? matchRequest(list, request.entries, matchItems) : matchFiles(list, matchItems, rule);
	}, [files, matchItems, rule, byRequest, request]);
	const assigned = useMemo(() => {
		const rows = files.map((f) => {
			const m = matched.byKey[f.key] || { status: 'unmatched', itemId: null, candidateIds: [] };
			const override = Object.prototype.hasOwnProperty.call(overrides, f.key) ? overrides[f.key] : undefined;
			const itemId = override !== undefined ? override : m.itemId;
			// The entry a file answers — its element is where the file goes.
			const entryIndex = override === undefined ? m.entryIndex ?? null : null;
			return { ...f, match: m, itemId, byHand: override !== undefined, entryIndex };
		});
		if (!byRequest) return rows;
		// A file given an item by hand takes an open entry of that item no other
		// file in this pass holds: one whose name or pattern it fits, else one
		// that takes any file, else none (the item's other attachments) — never
		// an entry meant for another kind of file (a PDF into Photos).
		const taken = new Set(rows.map(r => r.entryIndex).filter(i => i !== null));
		return rows.map((r) => {
			if (!r.itemId || r.entryIndex !== null) return r;
			const open = (request.entries || []).map((e, i) => ({ e, i }))
				.filter(({ e, i }) => e.itemId === r.itemId && !e.fulfilled && !taken.has(i));
			const name = `${r.file.name ?? ''}`.normalize('NFC').toLowerCase();
			const fits = open.find(({ e }) => (e.name ? `${e.name}`.normalize('NFC').toLowerCase() === name : e.pattern ? globMatches(e.pattern, r.file.name) : false))
				|| open.find(({ e }) => !e.name && !e.pattern);
			if (!fits) return r;
			taken.add(fits.i);
			return { ...r, entryIndex: fits.i };
		});
	}, [files, matched, overrides, byRequest, request]);
	const destinationOf = useCallback((a) => {
		if (!byRequest) return destination;
		const entry = a.entryIndex !== null && a.entryIndex !== undefined ? request.entries[a.entryIndex] : null;
		return entry?.element || null;
	}, [byRequest, request, destination]);

	// An entry's file column the request names (KPR-940): `{ entryId, column }`.
	const targetOf = useCallback((a) => {
		if (!byRequest) return null;
		const entry = a.entryIndex !== null && a.entryIndex !== undefined ? request.entries[a.entryIndex] : null;
		return entry?.entryId && entry?.column ? { entryId: entry.entryId, column: entry.column } : null;
	}, [byRequest, request]);

	const neededExisting = useMemo(() => [...new Set(assigned.map(a => a.itemId).filter(Boolean))], [assigned]);
	const missingExisting = neededExisting.filter(id => existing[id] === undefined);

	// What is already on each item a file is headed for: skip-if-present needs
	// it. Each item is asked for ONCE per opening: an answer must not be thrown
	// away because another answer changed the list of what is missing (the
	// first build re-asked every pending item each time one came back). Only
	// closing the panel — a new generation — discards what is in flight.
	const requestedRef = useRef(new Set());
	const generationRef = useRef(0);
	useEffect(() => {
		if (!open) { requestedRef.current = new Set(); generationRef.current += 1; }
	}, [open]);
	useEffect(() => {
		if (!open) return;
		const queue = missingExisting.filter(id => !requestedRef.current.has(id));
		if (!queue.length) return;
		queue.forEach(id => requestedRef.current.add(id));
		const generation = generationRef.current;
		const worker = async () => {
			while (queue.length) {
				const id = queue.shift();
				let list;
				try { list = await getItemAttachments(id); } catch { list = []; }
				if (generation === generationRef.current) setExisting(prev => ({ ...prev, [id]: list }));
			}
		};
		Promise.all(Array.from({ length: Math.min(EXISTING_CONCURRENCY, queue.length) }, worker));
	}, [open, missingExisting.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

	const plan = useMemo(() => {
		const destByKey = Object.fromEntries(assigned.map(a => [a.key, destinationOf(a)]));
		const targetByKey = Object.fromEntries(assigned.map(a => [a.key, targetOf(a)]));
		return planFiles({
			files: assigned.map(a => ({ key: a.key, file: a.file, itemId: a.itemId })),
			itemsById,
			destination,
			destinationLabel,
			destinationFor: byRequest ? key => destByKey[key] : null,
			targetFor: byRequest ? key => targetByKey[key] : null,
			elementFor,
			existingFor: id => existing[id] || [],
			attachAdmin,
		});
	}, [assigned, itemsById, destination, destinationLabel, byRequest, destinationOf, targetOf, elementFor, existing, attachAdmin]);
	const counts = summarise(plan);
	const checking = missingExisting.length > 0;

	// ---- choosing files
	const addFiles = useCallback((picked) => {
		const list = [...picked].filter(p => p?.file && !`${p.file.name}`.startsWith('.'));
		setFiles((prev) => {
			const room = MAX_FILES - prev.length;
			if (list.length > room) message.warning(`At most ${MAX_FILES} files at a time. ${formatCount(list.length - Math.max(0, room), 'file')} left out.`);
			return [...prev, ...list.slice(0, Math.max(0, room)).map(p => ({ key: keyFor(), file: p.file, path: p.path || p.file.name }))];
		});
	}, [message]);

	const onPick = (event) => {
		addFiles([...(event.target.files || [])].map(file => ({ file, path: file.webkitRelativePath || file.name })));
		event.target.value = '';
	};
	// The platform asks for the folder (a cancelled pick answers nothing).
	const onPickFolder = async () => {
		const picked = await pickFolder.pick();
		if (picked.length) addFiles(picked);
	};
	const onDrop = async (event) => {
		event.preventDefault();
		setDragging(false);
		if (phase !== 'review') return;
		addFiles(await filesFromDataTransfer(event.dataTransfer));
	};

	const removeFile = key => setFiles(prev => prev.filter(f => f.key !== key));
	const chooseItem = (key, itemId) => setOverrides(prev => ({ ...prev, [key]: itemId || null }));

	// ---- uploading
	const uploading = phase === 'uploading';
	useEffect(() => {
		if (!uploading) return undefined;
		const warn = (e) => { e.preventDefault(); e.returnValue = ''; return ''; };
		window.addEventListener('beforeunload', warn);
		return () => window.removeEventListener('beforeunload', warn);
	}, [uploading]);

	const start = async () => {
		const tasks = assigned.filter(a => plan[a.key]?.action === 'upload').map(a => ({ key: a.key, file: a.file, itemId: a.itemId, element: destinationOf(a), target: targetOf(a) }));
		if (!tasks.length) return;
		const controller = new AbortController();
		abortRef.current = controller;
		setPhase('uploading');
		const uploaded = {};
		const outcome = await runQueue(tasks, {
			signal: controller.signal,
			onState: (key, state) => setProgress(prev => ({ ...prev, [key]: state })),
			run: async (task, { onProgress, signal }) => {
				const att = await uploadItemAttachment(task.itemId, task.file, { onProgress, signal });
				uploaded[task.key] = att;
				return att;
			},
		});

		// Into a file element: one merge PUT per item and element, on a fresh read.
		// Into an entry's file column (KPR-940): one PATCH per entry and column.
		const bindFailures = [];
		const placed = [];
		{
			const byTarget = new Map();
			for (const t of tasks) {
				if (!t.element || !uploaded[t.key]?._id) continue;
				const target = `${t.itemId}\u0000${t.element}\u0000${t.target?.entryId || ''}\u0000${t.target?.column || ''}`;
				if (!byTarget.has(target)) byTarget.set(target, { itemId: t.itemId, name: t.element, entry: t.target, ups: [] });
				byTarget.get(target).ups.push({ key: t.key, id: uploaded[t.key]._id });
			}
			for (const { itemId, name, entry, ups } of byTarget.values()) {
				const group = elementFor(itemsById[itemId]?.cardId, name);
				const column = entry ? columnsOf(group).find(c => c?.name === entry.column) : null;
				const element = entry ? column : group;
				const multiple = element?.options?.allowMultiple === true;
				let where = null;
				const bind = async () => {
					const [fresh] = await fetchItemsByIds([itemId]);
					if (entry) {
						const list = fresh?.elements?.[name];
						const stored = entriesOf(list).find(e => e.id === entry.entryId);
						if (!stored) throw Object.assign(new Error('entry gone'), { entryGone: true });
						const current = fileIdsOf(stored.elements?.[entry.column]);
						const value = multiple ? [...current, ...ups.map(u => u.id)] : ups[0].id;
						where = `${entryName(group, entriesOf(list), stored)}, ${column?.label?.singular || entry.column}`;
						await updateEntry(itemId, name, entry.entryId, { elements: { [entry.column]: value }, ifUpdatedAt: fresh?.updatedAt || null });
						return;
					}
					const current = fileIdsOf(fresh?.elements?.[name]);
					const value = multiple ? [...current, ...ups.map(u => u.id)] : ups[0].id;
					await saveItemCells(itemId, { [name]: value }, { ifUpdatedAt: fresh?.updatedAt || null });
				};
				try {
					// eslint-disable-next-line no-await-in-loop
					try { await bind(); } catch (err) { if (isStaleItem(err)) await bind(); else throw err; }
					if (entry) placed.push({ itemId, keys: ups.map(u => u.key), where: `${group?.label?.singular || name}: ${where}` });
				} catch (err) {
					const words = err?.entryGone
						? 'That entry is no longer on this item.'
						: err?.response?.status === 428
						? 'This card asks for a signature, so files cannot be put in its elements from here. Put them in from the item.'
						: serverFileRefusal(err, element);
					bindFailures.push({ itemId, keys: ups.map(u => u.key), words });
				}
			}
		}

		abortRef.current = null;
		const summary = {
			attached: outcome.done.length - bindFailures.reduce((n, b) => n + b.keys.length, 0),
			failed: outcome.failed.map(f => ({ key: f.key, words: f.error?.response?.status === 409 && f.error?.response?.data?.code === 'item_locked'
				? (f.error.response.data.message || 'This item is locked.')
				: serverFileRefusal(f.error, null) })),
			unbound: bindFailures,
			placed,
			cancelled: outcome.cancelled.length,
		};
		setResult(summary);
		setPhase('done');
		if (outcome.done.length && typeof onAttached === 'function') onAttached(summary);
	};

	const stop = () => abortRef.current?.abort();

	const requestClose = () => {
		if (!uploading) { onClose(); return; }
		modal.confirm({
			title: 'Stop uploading?',
			content: 'Files already sent stay attached. The rest are not uploaded.',
			okText: 'Stop uploading',
			cancelText: 'Keep going',
			onOk: () => { stop(); onClose(); },
		});
	};

	// ---- words
	const scopeWords = request
		? `To the ${formatCount(items.length, 'item') || 'items'} asked for`
		: scope?.kind === 'ids'
		? `To the ${formatCount(items.length, 'selected item') || 'selected items'}`
		: `To the ${formatCount(items.length, 'item') || 'items'} listed`;
	const card = cardId => cardsById[cardId] || null;
	const badgeFor = (itemId) => {
		const it = itemsById[itemId];
		if (!it) return null;
		const c = card(it.cardId);
		return <RecordBadge kind="item" record={c} kicker={c?.name} title={titleFor(it.raw) || 'Untitled'} size="sm" />;
	};
	const itemOptions = useMemo(() => matchItems.map(it => ({ value: it.id, label: it.title || 'Untitled' })), [matchItems]);
	const byKeyName = Object.fromEntries(files.map(f => [f.key, f.path]));

	const ruleOptions = [
		...(request ? [{ value: 'request', label: 'As requested' }] : []),
		{ value: 'title', label: 'File name contains the title' },
		...rules.elements.map(el => ({ value: `element:${el.name}`, label: `File name is the ${el.label?.singular || el.name}` })),
		...(rules.externalId ? [{ value: 'externalId', label: 'File name is the import id' }] : []),
	];
	const ruleValue = rule.kind === 'element' ? `element:${rule.element}` : rule.kind;
	const onRule = (value) => {
		setOverrides({});
		if (value.startsWith('element:')) setRule({ kind: 'element', element: value.slice('element:'.length) });
		else setRule({ kind: value });
	};
	const destinationOptions = [
		{ value: '', label: 'Other attachments' },
		...fileElements.map(({ element, cards }) => ({
			value: element.name,
			label: cards.size > 1 || fileElements.filter(e => (e.element.label?.singular || e.element.name) === (element.label?.singular || element.name)).length > 1
				? `${element.label?.singular || element.name} (${[...cards].map(id => card(id)?.name).filter(Boolean).join(', ')})`
				: element.label?.singular || element.name,
		})),
	];

	const statusOf = (a) => {
		const p = plan[a.key];
		if (!a.itemId) return { tone: 'muted', words: a.match.status === 'ambiguous' ? `Matches ${formatCount(a.match.candidateIds.length, 'item')} — choose one` : 'No item matched — choose one, or remove the file' };
		if (p?.action === 'skip') return { tone: 'muted', words: p.words };
		if (p?.action === 'refuse') return { tone: 'danger', words: p.words };
		return null;
	};

	const nothingToDo = !files.length;
	const footer = phase === 'done' ? (
		<div className="bulk-attach__footer"><Button type="primary" onClick={onClose} data-action="bulk-attach-done">Done</Button></div>
	) : (
		<div className="bulk-attach__footer">
			{uploading
				? <Button onClick={stop} data-action="bulk-attach-stop">Stop</Button>
				: <Button onClick={requestClose}>Cancel</Button>}
			<Button
				type="primary"
				onClick={start}
				loading={uploading}
				disabled={uploading || checking || counts.upload === 0}
				data-action="bulk-attach-start"
			>
				{counts.upload ? `Attach ${formatCount(counts.upload, 'file')}` : 'Attach files'}
			</Button>
		</div>
	);

	return (
		<Drawer
			open={open}
			onClose={requestClose}
			title={(
				<span className="bulk-attach__title">
					<span aria-hidden="true" className="bulk-attach__title-glyph"><PAPERCLIP /></span> Attach files
				</span>
			)}
			placement="right"
			width={isMobile ? '100%' : 'min(720px, 100vw)'}
			destroyOnHidden
			footer={footer}
			className="bulk-attach"
			maskClosable={!uploading}
		>
			<DialogLoadState
				loaded={loaded}
				loading={loading}
				error={Boolean(loadError)}
				errorDetail={loadError || undefined}
				label="the items"
				onRetry={() => setAttempt(n => n + 1)}
				skeleton={<Skeleton variant="list-row" count={4} />}
				minHeight={200}
			>
				<>
					{request ? (
						<div className="bulk-attach__request">
							<Text strong>{`Asked for ${formatCount(request.counts?.entries || request.entries.length, 'file')}`}</Text>
							{request.counts?.fulfilled ? <Text type="secondary">{` · ${request.counts.fulfilled} already here`}</Text> : null}
							{request.createdVia?.label ? <Text type="secondary">{` · by ${request.createdVia.label}`}</Text> : null}
							{request.note ? <Paragraph className="bulk-attach__request-note">{request.note}</Paragraph> : null}
						</div>
					) : null}
					<Paragraph className="bulk-attach__scope">
						<Text strong>{scopeWords}</Text>
						{leftOut > 0 ? <Text type="secondary"> · {formatCount(leftOut, 'item')} cannot take files here (locked, read-only, or from another collection)</Text> : null}
					</Paragraph>
					{truncated ? (
						<Alert
							type="warning"
							showIcon
							className="bulk-attach__note"
							message="More items match than this can take at once"
							description="Only the first 1,000 are included. Narrow the filter, or select the items, to reach the rest."
						/>
					) : null}

					{phase === 'review' ? (
						<div
							className={`bulk-attach__drop${dragging ? ' bulk-attach__drop--over' : ''}`}
							onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
							onDragLeave={() => setDragging(false)}
							onDrop={onDrop}
							data-testid="bulk-attach-drop"
						>
							<p className="bulk-attach__drop-text">{isMobile ? 'Choose the files to attach.' : 'Drop files or a folder here.'}</p>
							<div className="bulk-attach__pickers">
								<Button onClick={() => fileInput.current?.click()} data-action="bulk-attach-choose">Choose files</Button>
								{!isMobile && pickFolder.available() ? <Button onClick={onPickFolder} data-action="bulk-attach-folder">Choose a folder</Button> : null}
							</div>
							<input ref={fileInput} type="file" multiple hidden onChange={onPick} data-testid="bulk-attach-input" />
							<Text type="secondary" className="bulk-attach__drop-hint">Nothing is uploaded until you press Attach.</Text>
						</div>
					) : null}

					{files.length ? (
						<>
							<div className="bulk-attach__controls">
								<label className="bulk-attach__control">
									<span>Match by</span>
									<Select value={ruleValue} onChange={onRule} options={ruleOptions} disabled={phase !== 'review'} aria-label="Match by" popupMatchSelectWidth={false} />
								</label>
								<label className="bulk-attach__control">
									<span>Put files in</span>
									<Select
										value={byRequest ? 'requested' : destination || ''}
										onChange={v => setDestination(v || null)}
										options={byRequest ? [{ value: 'requested', label: 'As requested' }] : destinationOptions}
										disabled={phase !== 'review' || byRequest}
										aria-label="Put files in"
										popupMatchSelectWidth={false}
									/>
								</label>
							</div>

							{phase === 'review' ? <Paragraph className="bulk-attach__summary" aria-live="polite">
								{checking ? 'Checking what is already attached…' : [
									counts.upload ? `${formatCount(counts.upload, 'file')} to attach` : null,
									counts.skip ? `${counts.skip} already there` : null,
									counts.refuse ? `${counts.refuse} refused` : null,
									counts.none ? `${formatCount(counts.none, 'file')} without an item` : null,
								].filter(Boolean).join(' · ')}
							</Paragraph> : null}

							{result ? <ResultNote result={result} names={byKeyName} itemTitle={id => titleFor(itemsById[id]?.raw)} /> : null}

							<ul className="bulk-attach__files" aria-label="Files">
								{assigned.map((a) => {
									const status = statusOf(a);
									const state = progress[a.key];
									return (
										<li key={a.key} className="bulk-attach__file" data-file={a.file.name}>
											<div className="bulk-attach__file-head">
												<span className="bulk-attach__file-name" title={a.path}>{a.file.name}</span>
												<span className="bulk-attach__file-size">{formatBytes(a.file.size)}</span>
												{phase === 'review' ? (
													<Tooltip title="Leave this file out">
														<Button size="small" type="text" icon={<CLOSE />} aria-label={`Leave ${a.file.name} out`} onClick={() => removeFile(a.key)} />
													</Tooltip>
												) : null}
											</div>
											<div className="bulk-attach__file-item">
												{phase === 'review' && (!a.itemId || a.byHand || a.match.status !== 'matched') ? (
													<Select
														size="small"
														showSearch
														allowClear
														value={a.itemId || undefined}
														placeholder={a.match.status === 'ambiguous' ? 'Choose which item' : 'Choose an item'}
														options={a.match.status === 'ambiguous' && !a.byHand
															? itemOptions.filter(o => a.match.candidateIds.includes(o.value)).concat(itemOptions.filter(o => !a.match.candidateIds.includes(o.value)))
															: itemOptions}
														optionFilterProp="label"
														onChange={v => chooseItem(a.key, v)}
														aria-label={`Item for ${a.file.name}`}
														className="bulk-attach__picker"
													/>
												) : badgeFor(a.itemId)}
												{phase === 'review' && a.itemId && !a.byHand && a.match.status === 'matched' ? (
													<Button size="small" type="link" onClick={() => chooseItem(a.key, a.itemId)} aria-label={`Change the item for ${a.file.name}`}>Change</Button>
												) : null}
											</div>
											{status ? <Text type={status.tone === 'danger' ? 'danger' : 'secondary'} className="bulk-attach__file-status">{status.words}</Text> : null}
											{state && state.status !== 'queued' ? (
												<Progress
													percent={Math.round((state.progress || 0) * 100)}
													size="small"
													status={state.status === 'failed' ? 'exception' : state.status === 'done' ? 'success' : 'active'}
													showInfo={false}
													aria-label={`${a.file.name}: ${state.status}`}
												/>
											) : null}
										</li>
									);
								})}
							</ul>

							{matched.itemsWithoutFile.length && phase === 'review' ? (
								<Collapse
									ghost
									size="small"
									className="bulk-attach__without"
									items={[{
										key: 'without',
										label: `${formatCount(matched.itemsWithoutFile.length, 'item')} without a file`,
										children: (
											<ul className="bulk-attach__without-list">
												{matched.itemsWithoutFile.slice(0, 200).map(id => <li key={id}>{badgeFor(id)}</li>)}
												{matched.itemsWithoutFile.length > 200 ? <li><Text type="secondary">and {matched.itemsWithoutFile.length - 200} more</Text></li> : null}
											</ul>
										),
									}]}
								/>
							) : null}
						</>
					) : null}

					{nothingToDo && !items.length ? <Paragraph type="secondary">No items here can take files. Select items you can edit, then try again.</Paragraph> : null}
				</>
			</DialogLoadState>
		</Drawer>
	);
}

function ResultNote({ result, names, itemTitle }) {
	const problems = result.failed.length + result.unbound.length;
	return (
		<Alert
			type={problems ? 'warning' : 'success'}
			showIcon
			className="bulk-attach__note"
			message={[
				result.attached ? `${formatCount(result.attached, 'file')} attached` : 'Nothing attached',
				result.failed.length ? `${result.failed.length} failed` : null,
				result.cancelled ? `${result.cancelled} not sent` : null,
			].filter(Boolean).join(' · ')}
			description={problems || (result.placed || []).length ? (
				<ul className="bulk-attach__failures">
					{result.failed.map(f => <li key={f.key}><Text strong>{names[f.key]}</Text>: {f.words}</li>)}
					{result.unbound.map(b => (
						<li key={b.itemId}>
							<Text strong>{itemTitle(b.itemId) || 'An item'}</Text>: {b.words} {formatCount(b.keys.length, 'file')} stayed on the item as other attachments.
						</li>
					))}
					{(result.placed || []).map(p => (
						<li key={`${p.itemId}${p.where}`}>
							<Text strong>{itemTitle(p.itemId) || 'An item'}</Text>: {formatCount(p.keys.length, 'file')} in {p.where}.
						</li>
					))}
				</ul>
			) : null}
		/>
	);
}
